import type { JSX } from 'preact';

interface IconProps extends Omit<JSX.SVGAttributes<SVGSVGElement>, 'size'> {
  size?: number;
}

function make(paths: JSX.Element, viewBox = '0 0 24 24') {
  return function Icon({ size = 18, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={viewBox}
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        {...rest}
      >
        {paths}
      </svg>
    );
  };
}

export const IconMenu = make(<path d="M4 6h16M4 12h16M4 18h16" />);
export const IconPlus = make(<path d="M12 5v14M5 12h14" />);
export const IconSettings = make(<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />);
export const IconSend = make(<path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />);
export const IconStop = make(<rect x="6" y="6" width="12" height="12" rx="2" />);
export const IconPlay = make(<path d="M6 4l14 8-14 8V4z" />);
export const IconTrash = make(<path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />);
export const IconSearch = make(
  <>
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.3-4.3" />
  </>,
);
export const IconCopy = make(
  <>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </>,
);
export const IconDownload = make(<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />);
export const IconCheck = make(<path d="M20 6L9 17l-5-5" />);
export const IconPopOut = make(<path d="M14 3h7v7M21 3l-9 9M18 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" />);
export const IconX = make(<path d="M18 6L6 18M6 6l12 12" />);
export const IconChevronDown = make(<path d="M6 9l6 6 6-6" />);
export const IconChevronUp = make(<path d="M18 15l-6-6-6 6" />);
export const IconCode = make(<path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />);
export const IconMessage = make(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />);
export const IconTerminal = make(<path d="M4 17l6-6-6-6M12 19h8" />);
export const IconPencil = make(<path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />);
export const IconRefresh = make(<path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />);
export const IconSparkles = make(<path d="M12 3l1.9 5.6 5.6 1.9-5.6 1.9L12 18l-1.9-5.6L4.5 10.5l5.6-1.9L12 3zM5 3v4M3 5h4M19 17v4M17 19h4" />);
export const IconBook = make(<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z" />);
export const IconUndo = make(<path d="M3 7v6h6M21 17a9 9 0 0 0-15-6.7L3 13" />);
export const IconRedo = make(<path d="M21 7v6h-6M3 17a9 9 0 0 1 15-6.7L21 13" />);
export const IconAlert = make(<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" />);
export const IconEye = make(
  <>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
export const IconEyeOff = make(
  <>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <path d="M2 2l20 20" />
  </>,
);
export const IconFix = make(<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />);

export function CueLogo({ size = 22 }: { size?: number }) {
  // Same geometry as public/favicon.svg: a C with a text caret waiting in its opening.
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <rect width="64" height="64" rx="15" fill="var(--brand)" />
      <path d="M39.5 21.2A14 14 0 1 0 39.5 42.8" fill="none" stroke="var(--brand-ink)" stroke-width="7" stroke-linecap="round" />
      <rect x="44" y="23" width="5.5" height="18" rx="2.75" fill="var(--brand-caret)" />
    </svg>
  );
}
